import { Money } from '@principle-theorem/accounting';
import {
  DepositForfeitureOutcomeStatus,
  MeasureDataType,
  MeasureFormatter,
} from '@principle-theorem/principle-core/interfaces';
import { QueryOptionResolver } from '../../query-option-resolvers';
import { MeasurePath } from '../data-accessor-factory';
import {
  type CanBeChartedProperty,
  type CanDoAllProperty,
  type CanQueryByTimestampProperty,
} from '../measure-properties';
import { MeasurePropertyFactory } from '../measure-property-factory';
import type { SchedulingEventFactMeasures } from './scheduling-event-fact';

export class DepositForfeitureEventMeasures {
  constructor(private readonly _parent: SchedulingEventFactMeasures) {}

  get defaultShouldForfeit(): CanDoAllProperty {
    return this._boolean(
      'defaultShouldForfeit',
      'Deposit Forfeiture Was Automatic',
      'Whether the selected reason and policy defaulted the deposit to forfeited.'
    );
  }

  get shouldForfeit(): CanDoAllProperty {
    return this._boolean(
      'shouldForfeit',
      'Deposit Forfeiture Selected',
      'Whether the staffer selected deposit forfeiture.'
    );
  }

  get overrideReasonProvided(): CanDoAllProperty {
    return this._boolean(
      'overrideReasonProvided',
      'Deposit Forfeiture Override Reason Provided',
      'Whether the staffer explained why automatic forfeiture was disabled.'
    );
  }

  get wasForfeited(): CanDoAllProperty {
    return this._boolean(
      'wasForfeited',
      'Deposit Was Forfeited',
      'Whether the approved deposit forfeiture completed successfully.'
    );
  }

  get configuredPercentage(): CanBeChartedProperty {
    const latestEvent = this._parent.latestEvent;
    const path = this._path('configuredPercentage');
    const measure = latestEvent.measureRef(path);
    return MeasurePropertyFactory.number(
      {
        id: this._id('configuredPercentage'),
        label: 'Configured Deposit Forfeiture Percentage',
        formatter: MeasureFormatter.Percentage,
      },
      measure,
      latestEvent.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get decidedAmount(): CanBeChartedProperty {
    return this._centsAsCurrency(
      'decidedAmountCents',
      'decidedAmount',
      'Deposit Forfeiture Amount Selected'
    );
  }

  get forfeitedAmount(): CanBeChartedProperty {
    return this._centsAsCurrency(
      'forfeitedAmountCents',
      'forfeitedAmount',
      'Deposit Amount Forfeited'
    );
  }

  get outcomeStatus(): CanDoAllProperty {
    const latestEvent = this._parent.latestEvent;
    const path = this._path('outcomeStatus');
    const measure = latestEvent.measureRef(path);
    return MeasurePropertyFactory.string(
      {
        id: this._id('outcomeStatus'),
        label: 'Deposit Forfeiture Outcome',
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.enum(
          DepositForfeitureOutcomeStatus
        ),
      },
      measure,
      latestEvent.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get forfeitedAt(): CanQueryByTimestampProperty {
    const latestEvent = this._parent.latestEvent;
    const path = MeasurePath.timestamp(this._path('forfeitedAt'));
    const measure = latestEvent.measureRef(path);
    return MeasurePropertyFactory.timestamp(
      {
        id: this._id('forfeitedAt'),
        label: 'Deposit Forfeited At',
        formatter: MeasureFormatter.Timestamp,
      },
      measure,
      latestEvent.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  private _boolean(
    propertyName: string,
    label: string,
    summary: string
  ): CanDoAllProperty {
    const latestEvent = this._parent.latestEvent;
    const path = this._path(propertyName);
    const measure = latestEvent.measureRef(path);
    return MeasurePropertyFactory.boolean(
      { id: this._id(propertyName), label, summary },
      measure,
      latestEvent.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  /**
   * Deposit amounts are persisted on the fact as integer cents, but
   * `MeasureFormatter.Currency` renders whatever number it is given as major
   * currency units. Convert to dollars on the way out so a 2500 cent
   * forfeiture reports as $25.00 rather than $2,500.00.
   */
  private _centsAsCurrency(
    propertyName: string,
    id: string,
    label: string
  ): CanBeChartedProperty {
    const latestEvent = this._parent.latestEvent;
    const path = this._path(propertyName);
    const measure = latestEvent.measureRef(path);
    return MeasurePropertyFactory.number(
      {
        id: this._id(id),
        label,
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Currency,
      },
      measure,
      latestEvent.buildQuery().attributes([measure.attributePath]).get(),
      0,
      (amountInCents) => Money.fromCents(amountInCents)
    );
  }

  private _id(propertyName: string): string {
    return `depositForfeiture.${propertyName}`;
  }

  private _path(propertyName: string): string {
    return `depositForfeiture.${propertyName}`;
  }
}
